import { useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  Sparkles, AlertTriangle, CheckCircle2, Info, TrendingUp,
  CalendarClock, ClipboardList, ArrowRight,
} from 'lucide-react'
import {
  PageHeader, SectionCard, Card, EmptyState, Badge, HealthBadge,
  KpiTile, KpiGrid, type BadgeTone,
} from '@/components/ui'
import { useStore } from '@/store/useStore'
import { daysUntil, date, num, todayIso} from '@/lib/format'
import type {
  Dataset, AccountInsight, EntityRef, InsightDriver, KeyDate,
  PropertyHealth, KeyDateStatus,
} from '@/types/entities'
import './Insights.css'

const TODAY = todayIso()

/* ---- WISE display order (deepest relationships first) ---- */
const ORDER = ['acct-jpm', 'acct-invesco', 'acct-buchanan', 'acct-heitman']

const DRIVER_ICON: Record<InsightDriver['tone'], React.ReactNode> = {
  ok: <CheckCircle2 size={15} />,
  warn: <AlertTriangle size={15} />,
  alert: <AlertTriangle size={15} />,
  neutral: <Info size={15} />,
}

const KEYDATE_TONE: Record<KeyDateStatus, BadgeTone> = {
  Overdue: 'alert', 'Due Soon': 'warn', Upcoming: 'info', Met: 'ok', Waived: 'neutral',
}

function routeForRef(d: Dataset, ref?: EntityRef): string | undefined {
  if (!ref) return undefined
  switch (ref.kind) {
    case 'account': return `/accounts/${ref.id}`
    case 'property': return `/properties/${ref.id}`
    case 'fund': return `/funds/${ref.id}`
    case 'track': {
      const t = d.tracks.find((x) => x.id === ref.id)
      return t ? `/accounts/${t.accountId}` : undefined
    }
    default: return undefined
  }
}

function labelForRef(d: Dataset, ref?: EntityRef): string | undefined {
  if (!ref) return undefined
  if (ref.label) return ref.label
  switch (ref.kind) {
    case 'account': return d.accounts.find((a) => a.id === ref.id)?.name
    case 'property': return d.properties.find((p) => p.id === ref.id)?.name
    case 'track': return d.tracks.find((t) => t.id === ref.id)?.name
    case 'fund': return d.funds.find((f) => f.id === ref.id)?.name
    default: return ref.id
  }
}

export default function Insights() {
  const data = useStore((s) => s.data)
  const navigate = useNavigate()

  const accountName = (id: string) => data.accounts.find((a) => a.id === id)?.name ?? id

  /* ---- WISE diagnostic cards, ordered ---- */
  const insights = useMemo(() => {
    const rank = new Map(ORDER.map((id, i) => [id, i]))
    return [...data.accountInsights].sort(
      (a, b) => (rank.get(a.accountId) ?? 99) - (rank.get(b.accountId) ?? 99),
    )
  }, [data])

  /* ---- Portfolio health distribution ---- */
  const verdictCounts = useMemo(() => {
    const c: Record<PropertyHealth, number> = { Healthy: 0, Watch: 0, 'At-Risk': 0 }
    for (const ins of insights) c[ins.verdict]++
    return c
  }, [insights])

  /* ---- Open-item aging rollup (portfolio + per account) ---- */
  const aging = useMemo(() => {
    const open = data.actionItems.filter((a) => a.status !== 'Closed')
    const ageOf = (createdAt?: string) => {
      if (!createdAt) return null
      const diff = (new Date(`${TODAY}T00:00:00`).getTime() - new Date(`${createdAt}T00:00:00`).getTime()) / 86_400_000
      return Math.max(0, Math.round(diff))
    }
    const ages = open.map((a) => ageOf(a.createdAt)).filter((x): x is number => x != null)
    const avgAge = ages.length ? Math.round(ages.reduce((s, x) => s + x, 0) / ages.length) : 0
    const oldestVal = ages.length ? Math.max(...ages) : 0
    const overdue = open.filter((a) => {
      const du = daysUntil(a.dueDate); return du != null && du < 0
    }).length

    // per-account rollup, resolving each item's ref to its owning account
    const refAccount = (ref: EntityRef): string | undefined => {
      if (ref.kind === 'account') return ref.id
      if (ref.kind === 'property') return data.properties.find((p) => p.id === ref.id)?.ownerAccountId
      if (ref.kind === 'track') return data.tracks.find((t) => t.id === ref.id)?.accountId
      if (ref.kind === 'fund') return data.funds.find((f) => f.id === ref.id)?.investorAccountIds[0]
      return undefined
    }
    const byAccount = new Map<string, { open: number; overdue: number }>()
    for (const a of open) {
      const acct = refAccount(a.attachedTo)
      if (!acct) continue
      const cur = byAccount.get(acct) ?? { open: 0, overdue: 0 }
      cur.open++
      const du = daysUntil(a.dueDate)
      if (du != null && du < 0) cur.overdue++
      byAccount.set(acct, cur)
    }
    const perAccount = [...byAccount.entries()]
      .map(([id, v]) => ({ id, name: accountName(id), ...v }))
      .sort((x, y) => y.overdue - x.overdue || y.open - x.open)

    return { total: open.length, avgAge, oldestVal, overdue, perAccount }
  }, [data])

  /* ---- Portfolio-wide critical-date radar (all key dates, soonest first) ---- */
  const radar = useMemo(
    () => [...data.keyDates].sort((a, b) => (a.date < b.date ? -1 : 1)),
    [data],
  )

  const noInsights = insights.length === 0

  return (
    <div className="ins">
      <PageHeader
        eyebrow="WISE Intelligence"
        title={<>Portfolio <b>Insights</b></>}
        sub="The diagnostic layer across every scored relationship — verdicts, evidence-led drivers, recommended focus, open-item aging, and the critical-date radar."
        actions={<span className="ins-badge"><Sparkles size={13} /> WISE assessed {insights.length} accounts</span>}
      />

      {/* ---- Portfolio health summary ---- */}
      <KpiGrid cols={4}>
        <KpiTile label="Accounts Scored" value={num(insights.length)} sub="active WISE coverage" />
        <KpiTile
          label="Healthy"
          value={num(verdictCounts.Healthy)}
          sub="no material concerns"
        />
        <KpiTile
          label="On Watch"
          value={num(verdictCounts.Watch)}
          accent={verdictCounts.Watch > 0}
          sub="monitoring drivers"
        />
        <KpiTile
          label="At-Risk"
          value={num(verdictCounts['At-Risk'])}
          accent={verdictCounts['At-Risk'] > 0}
          sub="requires intervention"
        />
      </KpiGrid>

      {/* ---- Diagnostic cards (main) + radar/aging (rail) ---- */}
      <div className="ins-split">
        <div className="ins-col-main">
          <SectionCard
            eyebrow="Diagnostics"
            title={<>Account <b>Verdicts</b></>}
            right={<Sparkles size={16} color="var(--wpi-cyan)" />}
          >
            {noInsights ? (
              <EmptyState
                icon={<Sparkles size={32} />}
                title="No WISE assessments yet"
                hint="Diagnostic verdicts are generated once enough operating data has accrued for an account."
              />
            ) : (
              <div className="ins-cards">
                {insights.map((ins) => (
                  <DiagnosticCard
                    key={ins.accountId}
                    ins={ins}
                    name={accountName(ins.accountId)}
                    onOpen={() => navigate(`/accounts/${ins.accountId}`)}
                  />
                ))}
              </div>
            )}
          </SectionCard>
        </div>

        <div className="ins-col-side">
          {/* Open-item aging rollup */}
          <SectionCard eyebrow="Hygiene" title={<>Open-Item <b>Aging</b></>}>
            <KpiGrid cols={2}>
              <KpiTile label="Open" value={num(aging.total)} />
              <KpiTile label="Overdue" value={num(aging.overdue)} accent={aging.overdue > 0} />
            </KpiGrid>
            <div className="ins-aging-meta">
              <div className="ins-meta-row"><span className="label">Avg Age</span><span className="num">{aging.avgAge}d</span></div>
              <div className="ins-meta-row"><span className="label">Oldest Open</span><span className="num">{aging.oldestVal}d</span></div>
            </div>
            {aging.perAccount.length > 0 && (
              <ul className="ins-aging-list">
                {aging.perAccount.map((a) => (
                  <li key={a.id} className="ins-aging-item">
                    <Link to={`/accounts/${a.id}`} className="ins-aging-name truncate">{a.name}</Link>
                    <span className="ins-aging-nums">
                      <span className="ins-aging-open num">{a.open} open</span>
                      {a.overdue > 0 && <Badge tone="alert" variant="soft">{a.overdue} late</Badge>}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>

          {/* Portfolio critical-date radar */}
          <SectionCard eyebrow="Radar" title={<>Critical <b>Dates</b></>}>
            {radar.length === 0 ? (
              <EmptyState icon={<CalendarClock size={26} />} title="No critical dates" />
            ) : (
              <ul className="ins-radar">
                {radar.map((kd) => (
                  <RadarRow key={kd.id} kd={kd} data={data} />
                ))}
              </ul>
            )}
          </SectionCard>
        </div>
      </div>
    </div>
  )
}

/* ---- Diagnostic card ---- */
function DiagnosticCard({
  ins, name, onOpen,
}: { ins: AccountInsight; name: string; onOpen: () => void }) {
  const topDriver =
    ins.drivers?.find((d) => d.tone === 'alert')
    ?? ins.drivers?.find((d) => d.tone === 'warn')
    ?? ins.drivers?.[0]
  const topRec =
    ins.recommended?.find((r) => r.priority === 'Critical')
    ?? ins.recommended?.find((r) => r.priority === 'High')
    ?? ins.recommended?.[0]

  return (
    <Card className="ins-card">
      <div className="ins-card-head">
        <div className="ins-card-id">
          <Link to={`/accounts/${ins.accountId}`} className="ins-card-name">{name}</Link>
          <HealthBadge health={ins.verdict} />
        </div>
        {ins.openItems && (
          <div className="ins-card-stat">
            <span className="num">{ins.openItems.total}</span>
            <span className="label">open · {ins.openItems.overdue} late</span>
          </div>
        )}
      </div>

      <p className="ins-card-narrative">{ins.narrative}</p>

      {topDriver && (
        <div className={`ins-card-driver ins-card-driver--${topDriver.tone}`}>
          <span className="ins-driver-icon">{DRIVER_ICON[topDriver.tone]}</span>
          <span><b>{topDriver.label}.</b> {topDriver.detail}</span>
        </div>
      )}

      {topRec && (
        <div className="ins-card-rec">
          <TrendingUp size={14} color="var(--wpi-blue-60)" />
          <span className="ins-rec-text">
            <b>{topRec.title}</b>
            {topRec.suggestedOwner && <span className="ins-rec-owner"> — {topRec.suggestedOwner}</span>}
          </span>
          {topRec.priority && <Badge tone={VERDICT_TONE_FOR_PRIORITY[topRec.priority] ?? 'neutral'} variant="soft">{topRec.priority}</Badge>}
        </div>
      )}

      <div className="ins-card-foot">
        <span className="ins-card-counts">
          <ClipboardList size={12} /> {ins.drivers.length} drivers · {ins.recommended.length} actions
        </span>
        <button className="ins-card-open" onClick={onOpen}>
          Open account <ArrowRight size={13} />
        </button>
      </div>
    </Card>
  )
}

const VERDICT_TONE_FOR_PRIORITY: Record<string, BadgeTone> = {
  Critical: 'alert', High: 'warn', Medium: 'info', Low: 'neutral',
}

/* ---- Radar row ---- */
function RadarRow({ kd, data }: { kd: KeyDate; data: Dataset }) {
  const du = daysUntil(kd.date)
  const overdue = du != null && du < 0
  const soon = du != null && du >= 0 && du <= 14
  const to = routeForRef(data, kd.attachedTo)
  const refLabel = labelForRef(data, kd.attachedTo)
  return (
    <li className="ins-radar-item">
      <span className="ins-radar-icon"><CalendarClock size={14} /></span>
      <div className="ins-radar-body">
        <span className="ins-radar-title truncate">{kd.title}</span>
        <span className="ins-radar-sub">
          <Badge tone={KEYDATE_TONE[kd.status]} variant="soft">{kd.type}</Badge>
          {refLabel && (
            to ? <Link to={to} className="ins-radar-ref">{refLabel}</Link> : <span className="ins-radar-ref">{refLabel}</span>
          )}
        </span>
      </div>
      <div className="ins-radar-when">
        <span className={`ins-radar-days num${overdue ? ' ins-radar-days--late' : soon ? ' ins-radar-days--soon' : ''}`}>
          {du == null ? '—' : overdue ? `${-du}d` : du === 0 ? 'now' : `${du}d`}
        </span>
        <span className="ins-radar-date">{date(kd.date, 'short')}</span>
      </div>
    </li>
  )
}
